import React, { useId, useState } from 'react';
import { Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { ButtonSplitGroup } from '@components/Buttons';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { ConfirmationModal } from '@components/Modals';
import { Page500 } from '@containers/ErrorPages';
import { userContactsApi } from '@reducers/user/contacts';
import { skipToken } from '@reduxjs/toolkit/query';

const schema = Yup.object({
  level: Yup.string().required(),
  contactType: Yup.string().required(),
  email: Yup.string().required(),
});

export const ContactDetailsForm = ({
  contactID,
  levelOptions,
  contactTypeOptions,
  mailSubscriptionOptions,
  empty = false,
}) => {
  let initialValues;

  const { data, isLoading, isError } = userContactsApi.useGetDetailsQuery(
    empty
      ? skipToken
      : {
          id: contactID,
        }
  );
  const [createQuery] = userContactsApi.useCreateContactMutation();
  const [deleteQuery] = userContactsApi.useDeleteContactMutation();
  const [updateQuery] = userContactsApi.useUpdateContactMutation();

  if (empty === true || isLoading) {
    initialValues = {
      level: String(levelOptions[0].value),
      contactType: contactTypeOptions[0].value,
      name: '',
      mobilePhone: '',
      fixedPhone: '',
      email: '',
      mailSubscription: [],
    };
  } else {
    initialValues = data;
  }

  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleCreateContact = (values, actions) => {
    if (contactID) {
      return;
    }
    createQuery({ data: values })
      .unwrap()
      .catch((error) => {
        actions.setErrors(error);
        actions.setSubmitting(false);
      });
  };

  const handleUpdateContact = (values, actions) => {
    if (!contactID) {
      return;
    }
    updateQuery({ data: values, id: contactID })
      .unwrap()
      .catch((error) => {
        actions.setErrors(error);
        actions.setSubmitting(false);
      });
  };

  const onSubmit = async (values, actions) => {
    actions.setSubmitting(true);
    handleCreateContact(values, actions);
    handleUpdateContact(values, actions);
    actions.setSubmitting(false);
  };
  const id = useId();
  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        isSubmitting,
      }) => {
        if (isLoading) {
          return <DefaultSpinnerCenter />;
        }
        if (isError) {
          return <Page500 />;
        }
        return (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <FloatingLabel
                  controlId={`floatingLevel-${id}`}
                  label="Level"
                  className="mb-1"
                >
                  <Form.Select
                    className="mb-1"
                    name="level"
                    value={values.level}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.level && !errors.level}
                    isInvalid={touched.level && errors.level}
                  >
                    {levelOptions.map((option) => (
                      <option key={option.value}>{option.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.level}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <FloatingLabel
                  controlId={`floatingContactType-${id}`}
                  label="Contact Type"
                  className="mb-1"
                >
                  <Form.Select
                    className="mb-1"
                    name="contactType"
                    value={values.contactType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.contactType && !errors.contactType}
                    isInvalid={touched.contactType && errors.contactType}
                  >
                    {contactTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.contactType}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <FloatingLabel
                  controlId={`floatingName-${id}`}
                  label="Name"
                  className="mb-1"
                >
                  <Form.Control
                    type="text"
                    placeholder="Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.name && !errors.name}
                    isInvalid={touched.name && errors.name}
                  />
                </FloatingLabel>
                <FloatingLabel
                  controlId={`floatingMobilePhone-${id}`}
                  label="Mobile Phone"
                  className="mb-1"
                >
                  <Form.Control
                    type="text"
                    placeholder="Mobile phone"
                    name="mobilePhone"
                    value={values.mobilePhone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.mobilePhone && !errors.mobilePhone}
                    isInvalid={touched.mobilePhone && errors.mobilePhone}
                  />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel
                  controlId={`floatingFixedPhone-${id}`}
                  label="Fixed Phone"
                  className="mb-1"
                >
                  <Form.Control
                    type="text"
                    placeholder="Fixed phone"
                    name="fixedPhone"
                    value={values.fixedPhone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.fixedPhone && !errors.fixedPhone}
                    isInvalid={touched.fixedPhone && errors.fixedPhone}
                  />
                </FloatingLabel>
                <FloatingLabel
                  controlId={`floatingEmail-${id}`}
                  label="Email"
                  className="mb-1"
                >
                  <Form.Control
                    type="text"
                    placeholder="Email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.email && !errors.email}
                    isInvalid={touched.email && errors.email}
                  />
                </FloatingLabel>
                <Form.Label
                  className="mt-2"
                  id={`mailSubscription-label-${id}`}
                >
                  Mail Subscription
                </Form.Label>
                <Form.Select
                  className="mb-1"
                  name="mailSubscription"
                  value={values.mailSubscription}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  multiple
                  htmlSize={6}
                  aria-labelledby={`mailSubscription-label-${id}`}
                >
                  {mailSubscriptionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row>
              <Col className="text-end mt-3" md={12}>
                <ConfirmationModal
                  message="Do you want to delete this contact?"
                  show={show}
                  onHide={handleClose}
                  handleConfirm={() => deleteQuery({ id: contactID })}
                />
                <ButtonSplitGroup>
                  {data ? (
                    <Button
                      variant="didxl-danger"
                      size="sm"
                      onClick={handleShow}
                    >
                      Delete
                    </Button>
                  ) : null}
                  <Button
                    variant="didxl"
                    size="sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </ButtonSplitGroup>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};
